import { useState, useEffect } from 'react';
import { NAVY, GOLD, WHITE, fmt, isOver, PRIORITY_COLORS, STATUS_COLORS, STATUSES, PRIORITIES } from '../lib/ui';
import { Pill, Avatar, Btn, Input, Select, Textarea, Field, Card, Empty, NoteText } from '../lib/ui';
import DocumentSection from './DocumentSection';
import { getDocuments } from '../lib/supabase';

export default function TaskDetail({ task: t, team, updateTask, addSubtask, updateSubtask, deleteSubtask, addTaskNote, deleteTaskNote, deleteTask, onBack }) {
  // Use local state so UI updates immediately
  const [localStatus,    setLocalStatus]    = useState(t.status);
  const [localPriority,  setLocalPriority]  = useState(t.priority);
  const [localDeadline,  setLocalDeadline]  = useState(t.deadline || '');
  const [localEstimated, setLocalEstimated] = useState(t.estimated || '');
  const [localSpent,     setLocalSpent]     = useState(t.spent || '');
  const [saving,         setSaving]         = useState(false);
  const [saved,          setSaved]          = useState(false);

  const [stForm,       setStForm]       = useState({ title:'', assignee_id:'', deadline:'' });
  const [showSt,       setShowSt]       = useState(false);
  const [noteText,     setNote]         = useState('');
  const [noteAuthor,   setNoteAuthor]   = useState(team[0]?.name||'');
  const [docs,         setDocs]         = useState([]);

  // Sync local state if task prop changes (e.g. after save)
  useEffect(() => {
    setLocalStatus(t.status);
    setLocalPriority(t.priority);
    setLocalDeadline(t.deadline || '');
    setLocalEstimated(t.estimated || '');
    setLocalSpent(t.spent || '');
  }, [t.id]);

  useEffect(() => {
    getDocuments('task', t.id).then(setDocs).catch(console.error);
  }, [t.id]);

  const [saveError, setSaveError] = useState('');

  const memberName = id => team.find(m=>m.id===id)?.name||'—';

  // Save all fields at once
  const handleSave = async () => {
    setSaving(true);
    setSaveError('');
    try {
      await updateTask({
        status:    localStatus,
        priority:  localPriority,
        deadline:  localDeadline || null,
        estimated: parseFloat(localEstimated) || null,
        spent:     parseFloat(localSpent) || null,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      setSaveError(e.message || 'Save failed. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleAddSt = () => {
    if (!stForm.title) return;
    addSubtask(stForm);
    setStForm({ title:'', assignee_id:'', deadline:'' });
    setShowSt(false);
  };

  const handleAddNote = () => {
    if (!noteText) return;
    addTaskNote({ text: noteText, author: noteAuthor });
    setNote('');
  };

  const toggleAssignee = (id) => {
    const cur = t.assignees || [];
    updateTask({ assignees: cur.includes(id) ? cur.filter(x=>x!==id) : [...cur, id] });
  };

  const done  = (t.subtasks||[]).filter(s=>s.status==='Completed').length;
  const total = (t.subtasks||[]).length;
  const pct   = total ? Math.round((done/total)*100) : null;

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:20 }}>
      <button onClick={onBack} style={{ alignSelf:'flex-start', background:'none', border:'1px solid #D0CBBF', borderRadius:6, padding:'5px 14px', fontSize:12, cursor:'pointer', color:NAVY, fontFamily:'inherit' }}>← Back to Matter</button>

      {/* Task header */}
      <div style={{ background:WHITE, borderRadius:10, border:'1px solid #E5E0D8', padding:'20px 24px' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:8 }}>
          <div style={{ fontFamily:'Georgia,serif', fontSize:20, fontWeight:700, color:NAVY }}>{t.title}</div>
          <button onClick={()=>{ if(window.confirm('Delete this task?')) deleteTask(); }}
            style={{ background:'none', border:'1px solid #C0392B', color:'#C0392B', borderRadius:6, padding:'5px 12px', cursor:'pointer', fontSize:12, fontFamily:'inherit' }}>Delete Task</button>
        </div>
        {t.description && <div style={{ fontSize:13, color:'#666', marginBottom:16, lineHeight:1.7 }}>{t.description}</div>}

        <div className="grid-4col" style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:14, marginBottom:16 }}>
          <Field label="Status">
            <Select value={localStatus} onChange={e => setLocalStatus(e.target.value)} style={{ fontSize:12, padding:'5px 8px' }}>
              {STATUSES.map(s=><option key={s}>{s}</option>)}
            </Select>
          </Field>
          <Field label="Priority">
            <Select value={localPriority} onChange={e => setLocalPriority(e.target.value)} style={{ fontSize:12, padding:'5px 8px' }}>
              {PRIORITIES.map(p=><option key={p}>{p}</option>)}
            </Select>
          </Field>
          <Field label="Deadline">
            <Input type="date" value={localDeadline} onChange={e => setLocalDeadline(e.target.value)} style={{ fontSize:12, padding:'5px 8px' }} />
          </Field>
          <Field label="Est. hours / Spent">
            <div style={{ display:'flex', gap:6 }}>
              <Input type="number" placeholder="Est" value={localEstimated} onChange={e => setLocalEstimated(e.target.value)} style={{ fontSize:12, padding:'5px 8px' }} />
              <Input type="number" placeholder="Spent" value={localSpent} onChange={e => setLocalSpent(e.target.value)} style={{ fontSize:12, padding:'5px 8px' }} />
            </div>
          </Field>
        </div>

        {/* Save button */}
        <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:16 }}>
          <button onClick={handleSave} disabled={saving} style={{
            background: saved ? '#27AE60' : NAVY, color: saved ? WHITE : GOLD,
            border:'none', borderRadius:7, padding:'9px 24px', fontSize:13, fontWeight:700,
            cursor: saving ? 'not-allowed' : 'pointer', fontFamily:'inherit', transition:'background 0.2s'
          }}>
            {saving ? 'Saving…' : saved ? '✓ Saved' : 'Save Changes'}
          </button>
          {saved && <span style={{ fontSize:12, color:'#27AE60' }}>Task updated successfully</span>}
          {saveError && <span style={{ fontSize:12, color:'#C0392B' }}>⚠ {saveError}</span>}
        </div>

        {/* Assignees */}
        <Field label="Assigned To">
          <div style={{ display:'flex', gap:10, flexWrap:'wrap', paddingTop:6 }}>
            {team.map(m => {
              const active = (t.assignees||[]).includes(m.id);
              return (
                <label key={m.id} style={{ display:'flex', alignItems:'center', gap:6, fontSize:13, cursor:'pointer',
                  background:active?NAVY+'11':'transparent', borderRadius:20, padding:'4px 10px',
                  border:active?`1px solid ${NAVY}33`:'1px solid transparent' }}>
                  <input type="checkbox" checked={active} onChange={()=>toggleAssignee(m.id)} style={{ display:'none' }} />
                  <Avatar name={m.name} size={22} />
                  <span style={{ fontWeight:active?700:400, color:active?NAVY:'#555' }}>{m.name}</span>
                </label>
              );
            })}
          </div>
        </Field>
      </div>

      {/* Subtasks */}
      <Card title={`Subtasks / Workstreams${total ? ` (${done}/${total} done${pct!==null ? ` · ${pct}%` : ''})` : ''}`}>
        {total>0 && (
          <div style={{ height:4, background:'#F0EDE8', borderRadius:2, marginBottom:12 }}>
            <div style={{ height:'100%', width:`${pct}%`, background:pct===100?'#27AE60':GOLD, borderRadius:2, transition:'width 0.3s' }} />
          </div>
        )}
        {(t.subtasks||[]).length===0 && !showSt && <Empty text="No subtasks yet." />}
        {(t.subtasks||[]).map(s => (
          <div key={s.id} style={{ display:'flex', gap:10, alignItems:'center', padding:'10px 0', borderBottom:'1px solid #F0EDE8' }}>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:13, fontWeight:600, color:NAVY }}>{s.title}</div>
              <div style={{ fontSize:11, color:'#888' }}>{memberName(s.assignee_id)} · Due {fmt(s.deadline)}</div>
              {s.notes && <div style={{ fontSize:12, color:'#666', marginTop:4, fontStyle:'italic' }}>{s.notes}</div>}
            </div>
            <Select value={s.status} onChange={e=>updateSubtask(s.id,{status:e.target.value})} style={{ fontSize:11, padding:'4px 8px', width:150 }}>
              {STATUSES.map(st=><option key={st}>{st}</option>)}
            </Select>
            {isOver(s.deadline) && s.status!=='Completed' && <Pill label="Overdue" color="#C0392B" size={10} />}
            <button onClick={()=>{ if(window.confirm('Delete subtask?')) deleteSubtask(s.id); }}
              style={{ background:'none', border:'none', color:'#CCC', cursor:'pointer', fontSize:18, lineHeight:1 }}>×</button>
          </div>
        ))}
        {!showSt ? (
          <button onClick={()=>setShowSt(true)} style={{ marginTop:10, background:'none', border:`1px dashed ${GOLD}`, borderRadius:6, padding:'8px 16px', color:GOLD, cursor:'pointer', fontSize:12, fontWeight:600, width:'100%' }}>+ Add Subtask / Workstream</button>
        ) : (
          <div style={{ marginTop:12, display:'grid', gridTemplateColumns:'1fr 1fr 1fr auto', gap:10, alignItems:'end' }}>
            <Input placeholder="Subtask title" value={stForm.title} onChange={e=>setStForm({...stForm,title:e.target.value})} />
            <Select value={stForm.assignee_id} onChange={e=>setStForm({...stForm,assignee_id:e.target.value})}>
              <option value="">Assignee…</option>
              {team.map(m=><option key={m.id} value={m.id}>{m.name}</option>)}
            </Select>
            <Input type="date" value={stForm.deadline} onChange={e=>setStForm({...stForm,deadline:e.target.value})} />
            <div style={{ display:'flex', gap:6 }}>
              <Btn onClick={handleAddSt}>Add</Btn>
              <Btn variant="ghost" onClick={()=>setShowSt(false)}>✕</Btn>
            </div>
          </div>
        )}
      </Card>

      {/* Task Notes */}
      <Card title="Task Notes">
        {(t.task_notes||[]).length===0 && <Empty text="No notes yet." />}
        {(t.task_notes||[]).map(n => (
          <div key={n.id} style={{ display:'flex', alignItems:'flex-start', gap:10, padding:'12px 0', borderBottom:'1px solid #F0EDE8' }}>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:11, color:'#999', marginBottom:6 }}>{n.author} · {fmt(n.created_at?.slice(0,10))}</div>
              <NoteText text={n.text} />
            </div>
            <button onClick={()=>{ if(window.confirm('Delete note?')) deleteTaskNote(n.id); }}
              style={{ background:'none', border:'none', color:'#DDD', cursor:'pointer', fontSize:16, lineHeight:1 }}>×</button>
          </div>
        ))}
        <div style={{ marginTop:12, display:'flex', flexDirection:'column', gap:8 }}>
          <div style={{ display:'flex', gap:8, alignItems:'center' }}>
            <Select value={noteAuthor} onChange={e=>setNoteAuthor(e.target.value)} style={{ width:140, fontSize:12, padding:'6px 8px' }}>
              {team.map(m=><option key={m.id} value={m.name}>{m.name}</option>)}
            </Select>
          </div>
          <Textarea rows={5} placeholder="Write a note… use * for bullets, 1. for numbered lists" value={noteText} onChange={e=>setNote(e.target.value)} style={{ flex:1 }} />
          <Btn onClick={handleAddNote}>Add Note</Btn>
        </div>
      </Card>

      {/* Documents */}
      <DocumentSection
        linkedType="task"
        linkedId={t.id}
        team={team}
        docs={docs}
        onDocsChange={setDocs}
      />
    </div>
  );
}
